import React from 'react'
import { isListingDurationAvailable } from '@popety-io/utils'
import { Icon } from '../../../atoms/Icon'
import { ListingEvent as RentalEventType } from './event.types'
import { truncateText } from './formatUtils'
import {
  EventCard,
  EventHeader,
  EventTitle,
  EventIcon,
  EventStatus,
  EventContent,
  EventDetails,
  EventImage,
  EventPrice,
  EventPriceSub,
  EventDescription,
  EventMeta,
  MetaItem,
  MetaIcon,
  EventFooter,
} from '../HistoryTab.styles'

interface RentalEventProps {
  event: RentalEventType
  translations?: {
    title?: string
    fields?: {
      address?: string
      propertyType?: string
      propertyCategory?: string
      area?: string
      rooms?: string
      bathrooms?: string
      floor?: string
      constructionYear?: string
      advertiser?: string
      broker?: string
      availableDate?: string
      furnished?: string
      petsAllowed?: string
      parking?: string
      balcony?: string
      terrace?: string
      garden?: string
      energyLabel?: string
    }
    status?: {
      active?: string
      inactive?: string
    }
    footer?: {
      online?: string
      days?: string
    }
  }
  formatPrice?: (price: number) => string
  formatPricePerSqm?: (price: number) => string
  formatDate?: (date: string) => string
}

export const RentalEventComponent: React.FC<RentalEventProps> = ({
  event,
  translations,
  formatPrice,
  formatPricePerSqm,
  formatDate,
}) => {
  const features = []
  if (event.furnished)
    features.push(translations?.fields?.furnished || 'Furnished')
  if (event.petsAllowed)
    features.push(translations?.fields?.petsAllowed || 'Pets allowed')
  if (event.parking) features.push(translations?.fields?.parking || 'Parking')
  if (event.balcony) features.push(translations?.fields?.balcony || 'Balcony')
  if (event.terrace) features.push(translations?.fields?.terrace || 'Terrace')
  if (event.garden) features.push(translations?.fields?.garden || 'Garden')

  const statusText =
    event.status === 'Active'
      ? translations?.status?.active || 'Active'
      : translations?.status?.inactive || 'Inactive'

  return (
    <EventCard $type="rental">
      <EventHeader>
        <EventTitle>
          <EventIcon>
            <Icon name="listing-rental" size="small" />
          </EventIcon>
          {translations?.title || 'Rental Listing'}
        </EventTitle>
        <EventStatus $status={event.status}>{statusText}</EventStatus>
      </EventHeader>

      <EventContent>
        <EventDetails>
          {event.price && event.price > 0 && (
            <>
              <EventPrice>{formatPrice(event.price)}</EventPrice>
              {event.pricePerSqm && (
                <EventPriceSub>
                  {formatPricePerSqm(event.pricePerSqm)}
                </EventPriceSub>
              )}
            </>
          )}

          {/* Truncate description to 100 characters */}
          {event.description && (
            <EventDescription>
              {truncateText(event.description, 100)}
            </EventDescription>
          )}

          <EventMeta>
            {event.propertyType && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </MetaIcon>
                {event.propertyType}
                {event.propertyCategory && ` - ${event.propertyCategory}`}
              </MetaItem>
            )}

            {event.area !== undefined && event.area > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M4 4h16v16H4z" />
                </MetaIcon>
                {event.area} m²
              </MetaItem>
            )}

            {event.rooms !== undefined && event.rooms > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </MetaIcon>
                {event.rooms} {translations?.fields?.rooms || 'rooms'}
              </MetaItem>
            )}

            {event.bathrooms !== undefined && event.bathrooms > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 7v4.5l-1.5 1.5L6 11.5V7c0-.55.45-1 1-1h1c.55 0 1 .45 1 1z" />
                </MetaIcon>
                {event.bathrooms}{' '}
                {translations?.fields?.bathrooms || 'bathrooms'}
              </MetaItem>
            )}

            {event.floor !== undefined && event.floor > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
                </MetaIcon>
                {translations?.fields?.floor || 'Floor'} {event.floor}
              </MetaItem>
            )}

            {event.constructionYear && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </MetaIcon>
                {translations?.fields?.constructionYear || 'Built'}:{' '}
                {event.constructionYear}
              </MetaItem>
            )}

            {event.availableDate && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                </MetaIcon>
                {translations?.fields?.availableDate || 'Available'}:{' '}
                {formatDate(event.availableDate)}
              </MetaItem>
            )}

            {event.energyLabel && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.48 2.54l2.6 1.53c.56-1.24.88-2.62.88-4.07 0-5.18-3.95-9.45-9-9.95zM12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.06.5-9 4.76-9 9.95 0 5.52 4.47 10 9.99 10 3.31 0 6.24-1.61 8.06-4.09l-2.6-1.53C16.17 17.98 14.21 19 12 19z" />
                </MetaIcon>
                {translations?.fields?.energyLabel || 'Energy'}:{' '}
                {event.energyLabel}
              </MetaItem>
            )}

            {features.length > 0 && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </MetaIcon>
                {features.join(', ')}
              </MetaItem>
            )}

            {event.brokerName && (
              <MetaItem>
                <MetaIcon viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </MetaIcon>
                {translations?.fields?.broker || 'Broker'}: {event.brokerName}
                {event.brokerCompany && ` (${event.brokerCompany})`}
              </MetaItem>
            )}
          </EventMeta>
        </EventDetails>

        {event.imageUrls && event.imageUrls[0] && (
          <EventImage>
            <img
              src={event.imageUrls[0]}
              alt={event.propertyType || 'Property'}
            />
          </EventImage>
        )}
      </EventContent>

      {isListingDurationAvailable(event.onlineDays) && (
        <EventFooter>
          {translations?.footer?.online || 'Online'} {event.onlineDays}{' '}
          {translations?.footer?.days || 'days'}
        </EventFooter>
      )}
    </EventCard>
  )
}
